//@flow
import React, { Component } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import type { IntlShape } from 'react-intl';
import moment from 'moment';
import { connect } from 'react-redux';
import { reduxForm, Field, Form, type FormProps } from 'redux-form';

import { bindActionCreatorsForm } from 'util/safeDispatch';
import type {
  Catalog,
  CatalogProperties,
  CatalogType,
  Dispatch,
  Role,
} from 'state/types';
import localizedStrings from 'util/localizedStrings';
import StaticTextField from 'view/Forms/StaticTextField';
import {
  CATALOG_PROPERTY_KEY,
  CATALOG_STATUS,
  CATALOG_STORAGE_TYPE,
  CATALOG_TEMPLATES_PANEL_ID,
} from 'util/constants';
import {
  getActivePanelId,
  getCatalogProperties,
  getCatalogs,
  getCatalogTypes,
  getDefaultRole,
  getRoles,
} from 'state/selectors';
import { setGlobalSettings } from 'state/commands/adminPropertiesCommands';
import {
  createCatalogTemplate as createCatalogTemplateAction,
  getCatalogTypes as getCatalogTypesAction,
  getCatalogProperties as getCatalogPropertiesAction,
  renameCatalog,
} from 'state/commands/catalogCommands';
import { showDialogPanel } from 'state/commands/dialogPanelCommands';
import { catalogNameExists, isLegalCatalogName } from 'util/catalogUtils';
import ModalStringPrompt from 'view/App/DialogPanels/ModalStringPrompt';
import LabeledField from 'view/Forms/LabeledField';
import { getRoles as getRolesAction } from 'state/commands/roleCommands';
import { getLocalizedRoleName } from 'util/roleUtils';
import { GLOBAL_SETTING } from 'util/serverUtils';

import CatalogTemplatesDialog from './CatalogTemplatesDialog';

const storageTypeKeyToString = {
  [CATALOG_STORAGE_TYPE.FILESYSTEM]:
    localizedStrings.CATALOG_STORAGE_TYPE_WATCHFOLDERS,
  [CATALOG_STORAGE_TYPE.VAULT]: localizedStrings.CATALOG_STORAGE_TYPE_VAULT,
  [CATALOG_STORAGE_TYPE.S3]: localizedStrings.CATALOG_STORAGE_TYPE_S3,
};

const statusKeyToString = {
  [CATALOG_STATUS.OFFLINE]: localizedStrings.CATALOG_OFFLINE,
  [CATALOG_STATUS.ONLINE]: localizedStrings.CATALOG_ONLINE,
};

type Props = {
  catalog: Catalog,
  catalogs: Catalog[],
  catalogProperties: CatalogProperties,
  catalogTypes: CatalogType[],
  defaultRole: Role,
  getCatalogPropertiesAction: typeof getCatalogPropertiesAction,
  getCatalogTypesAction: typeof getCatalogTypes,
  intl: IntlShape,
  renameCatalog: typeof renameCatalog,
  roles: Role[],
} & FormProps;

type State = {
  isCreateTemplateVisible: boolean,
};

class CatalogDetailsPage extends Component<Props, State> {
  closeCreateTemplatePrompt: Function;
  doCreateTemplate: Function;
  handleChange: Function;
  handleCreateCatalogTemplate: Function;
  handleDefaultRoleChange: Function;
  handleKeyDown: Function;
  showManageTemplatesDialog: Function;
  submit: Function;
  templateNameValidator: Function;

  state = { isCreateTemplateVisible: false };

  constructor() {
    super();
    this.closeCreateTemplatePrompt = this.closeCreateTemplatePrompt.bind(this);
    this.doCreateTemplate = this.doCreateTemplate.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleCreateCatalogTemplate = this.handleCreateCatalogTemplate.bind(
      this
    );
    this.handleDefaultRoleChange = this.handleDefaultRoleChange.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.showManageTemplatesDialog = this.showManageTemplatesDialog.bind(this);
    this.submit = this.submit.bind(this);
    this.templateNameValidator = this.templateNameValidator.bind(this);
  }

  componentDidMount() {
    const { catalog } = this.props;
    if (catalog) {
      this.props.getCatalogPropertiesAction(catalog.catalogId, [
        CATALOG_PROPERTY_KEY.CATALOG_DETAILS,
      ]);
      this.props.getCatalogTypesAction(catalog.catalogId);
      this.props.getRolesAction();
      this.props.initialize({ name: catalog.name });
    }
  }

  async submit({ name }) {
    const {
      catalog,
      intl: { formatMessage },
    } = this.props;
    const successMessage = formatMessage(
      localizedStrings.NOTIFICATION_SUCCESS_UPDATE
    );
    // server expects "id" instead of "catalogId" (see PORT-13172)
    const newCatalog = {
      ...catalog,
      id: catalog.catalogId,
      name,
    };

    const newName = await this.props.renameCatalog(newCatalog, successMessage);
    this.props.initialize({ name: newName });
  }

  handleChange(event, newValue) {
    if (!isLegalCatalogName(newValue)) {
      event.preventDefault();
    }
  }

  handleKeyDown(event: SyntheticKeyboardEvent<HTMLInputElement>) {
    const { target } = event;
    const blur = () => {
      if (target instanceof HTMLInputElement) {
        setTimeout(() => target.blur());
      }
    };

    switch (event.key) {
      case 'Escape':
        this.props.reset();
        blur();
        break;
      case 'Enter':
        if (this.props.valid) {
          blur();
        }
        break;
      default:
      // do nothing
    }
  }

  handleCreateCatalogTemplate() {
    //remove focus from button, otherwise it gets clicked again when submitting modal
    const b = document.activeElement;
    b && b.blur();
    this.setState({ isCreateTemplateVisible: true });
  }

  closeCreateTemplatePrompt() {
    this.setState({ isCreateTemplateVisible: false });
  }

  doCreateTemplate(name) {
    const {
      catalog,
      createCatalogTemplateAction,
      intl: { formatMessage },
    } = this.props;
    const msg = formatMessage(localizedStrings.NOTIFICATION_SUCCESS_UPDATE);
    createCatalogTemplateAction(catalog.catalogId, name, msg);
  }

  templateNameValidator(value) {
    if (!value || !value.length) {
      return 'Empty String';
    } else if (
      this.props.catalogTypes
        .map(template => template.name.toLowerCase())
        .includes(value.toLowerCase())
    ) {
      return 'Existing Template';
    }
  }

  showManageTemplatesDialog() {
    this.props.showDialogPanel(CATALOG_TEMPLATES_PANEL_ID);
  }

  handleDefaultRoleChange(e) {
    const newRoleName = e.currentTarget.value;
    this.props.setGlobalSettings({
      [GLOBAL_SETTING.DEFAULT_USER_ACCESS_LEVEL]: newRoleName,
    });
  }

  render() {
    const {
      activePanelId,
      catalog,
      catalogProperties,
      defaultRole,
      handleSubmit,
      intl,
      intl: { formatDate, formatTime, formatMessage },
      invalid,
      pristine,
      roles,
      submitting,
    } = this.props;

    const catalogDetails =
      catalogProperties && catalogProperties.catalogDetails;

    if (!catalogDetails || !catalog) {
      return null;
    } else {
      const createdDate = new Date(
        moment(catalogDetails.creationDate).toString()
      );
      const staticFormData = [
        {
          label: formatMessage(localizedStrings.CATALOG_STATUS),
          value: formatMessage(statusKeyToString[catalogDetails.status]),
        },
        {
          label: formatMessage(localizedStrings.CATALOG_STORAGE_TYPE_LABEL),
          value: formatMessage(
            storageTypeKeyToString[catalogDetails.storageType]
          ),
        },
        {
          label: formatMessage(localizedStrings.ASSETS),
          value: catalogDetails.assetCount,
        },
        {
          label: formatMessage(localizedStrings.USERS),
          value: `${catalogDetails.numberOfUsersConnected} / ${
            catalogDetails.numberOfMembers
          }`,
        },
        {
          label: formatMessage(localizedStrings.CATALOG_DETAILS_CREATION_DATE),
          value: `${formatDate(createdDate)} ${formatTime(createdDate)}`,
        },
      ];

      return (
        <div
          className="portfolio-form-container is-textured"
          data-test-name="general-catalog-page"
        >
          <Form onSubmit={handleSubmit(this.submit)} className="box">
            <div className="field is-horizontal has-addons">
              <div className="field-label is-normal is-medium">
                <label className="label">
                  <FormattedMessage {...localizedStrings.NAME} />
                </label>
              </div>
              <div className="field-body">
                <div className="control">
                  <Field
                    name="name"
                    className="input is-medium"
                    component="input"
                    onChange={this.handleChange}
                    onKeyDown={this.handleKeyDown}
                    style={{ width: '400px' }}
                    type="text"
                    data-test-name="catalog-name"
                  />
                </div>
                <div className="control">
                  <button
                    className={`button is-primary is-medium ${
                      submitting ? 'is-loading' : ''
                    }`}
                    disabled={pristine || invalid}
                    type="submit"
                    data-test-name="submit"
                  >
                    <FormattedMessage {...localizedStrings.APPLY} />
                  </button>
                </div>
              </div>
            </div>
            {staticFormData.map(data => (
              <StaticTextField
                key={data.label}
                label={data.label}
                value={data.value}
              />
            ))}
          </Form>

          <div className="box">
            <div className="subtitle">
              <FormattedMessage {...localizedStrings.CATALOG_TEMPLATES_TITLE} />
            </div>
            <div className="buttons dir-svc-desc-form-button-group">
              <button
                className="button"
                onClick={this.handleCreateCatalogTemplate}
                data-test-name="create-template"
              >
                <FormattedMessage {...localizedStrings.CATALOG_SAVE_TEMPLATE} />
              </button>
              <button
                className="button"
                onClick={this.showManageTemplatesDialog}
                data-test-name="manage-templates"
              >
                <FormattedMessage
                  {...localizedStrings.CATALOG_MANAGE_TEMPLATES_MENU_LABEL}
                />
              </button>
            </div>
            <span className="help">
              <FormattedMessage
                {...localizedStrings.CATALOG_SAVE_TEMPLATE_DETAILS_TEXT}
              />
            </span>
          </div>

          <div className="box">
            <div className="subtitle">
              <FormattedMessage
                {...localizedStrings.GLOBAL_SETTINGS_CATALOG_DEFAULTS}
              />
            </div>
            <LabeledField
              label={formatMessage(localizedStrings.DEFAULT_ACCESS_LEVEL)}
            >
              <div className="select">
                <select
                  className="select"
                  component="select"
                  data-test-name="default-user-role"
                  id="accessLevel"
                  name="accessLevel"
                  onChange={this.handleDefaultRoleChange}
                  type="select"
                  value={defaultRole && defaultRole.name}
                >
                  {roles.map(role => (
                    <option key={role.name} value={role.name}>
                      {getLocalizedRoleName(intl, role.name)}
                    </option>
                  ))}
                </select>
              </div>
            </LabeledField>
          </div>

          <ModalStringPrompt
            dataTestName="create-template-modal"
            isOpen={this.state.isCreateTemplateVisible}
            label={formatMessage(localizedStrings.NAME)}
            onClose={this.closeCreateTemplatePrompt}
            onSave={this.doCreateTemplate}
            panelTitle={formatMessage(
              localizedStrings.CATALOG_SAVE_TEMPLATE_TITLE
            )}
            maxLength={100}
            validate={this.templateNameValidator}
          />
          {activePanelId === CATALOG_TEMPLATES_PANEL_ID && (
            <CatalogTemplatesDialog />
          )}
        </div>
      );
    }
  }
}

function validate(data: Object, props: Props) {
  const errors = {};
  const maxFieldLen = 255;

  if (!data.name || data.name.length > maxFieldLen) {
    errors.name = 'Invalid';
  } else if (catalogNameExists(props.catalogs, data.name)) {
    errors.name = 'Duplicate';
  }

  return errors;
}

const detailsForm = reduxForm({ form: 'CATALOG_DETAILS_FORM', validate })(
  CatalogDetailsPage
);

export default connect(
  state => ({
    activePanelId: getActivePanelId(state),
    catalogProperties: getCatalogProperties(state),
    catalogs: getCatalogs(state),
    catalogTypes: getCatalogTypes(state),
    defaultRole: getDefaultRole(state),
    roles: getRoles(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsForm({
      createCatalogTemplateAction,
      getCatalogPropertiesAction,
      getCatalogTypesAction,
      getRolesAction,
      renameCatalog,
      setGlobalSettings,
      showDialogPanel,
    })
)(injectIntl(detailsForm));



// WEBPACK FOOTER //
// ./src/view/Pages/CatalogsPage/CatalogDetailsPage.js